import { useCallback, useEffect } from 'react';

import { EwusPatient } from '@dreryk/egabinet-ewus-frontend-api';

import { useBoolean } from '@dreryk/react-common';
import {
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   ModalCloseButton,
   ModalConfirmButton,
} from '@dreryk/react-components';

import { downloadPersonalDataFromEwusTranslationKeys } from '../../../translations';

import { EwusPatientComparisonModal } from './EwusPatientComparisonModal';

type PatientReplaceWithEwusPatientProps = {
   readonly ewusPatient: EwusPatient;
   readonly patient: {
      firstName: string | null;
      lastName: string | null;
   };
   readonly onPatientReplace: (ewusPatient: EwusPatient) => void;
   readonly onPatientReplaceReject: VoidFunction;
};

export const PatientReplaceWithEwusPatient = (props: PatientReplaceWithEwusPatientProps): JSX.Element | null => {
   const [{ setTrue: openComparisonModal, setFalse: closeComparisonModal }, comparisonModalOpened] = useBoolean(false);
   const { ewusPatient } = props;

   const shouldReplaceImmediately = !props.patient.firstName && !props.patient.lastName;

   useEffect(() => {
      if (ewusPatient && shouldReplaceImmediately) {
         props.onPatientReplace(props.ewusPatient);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ewusPatient]);

   useEffect(() => {
      if (ewusPatient && !shouldReplaceImmediately) {
         openComparisonModal();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [shouldReplaceImmediately]);

   const handlePatientReplaceReject = useCallback(() => {
      closeComparisonModal();
      props.onPatientReplaceReject();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onPatientReplaceReject, closeComparisonModal]);

   if (!comparisonModalOpened) {
      return null;
   }

   return (
      <EwusPatientComparisonModal ewusPatient={ewusPatient} patient={props.patient}>
         <>
            <ModalConfirmButton
               variant={ButtonVariant.SECONDARY}
               label={downloadPersonalDataFromEwusTranslationKeys.leaveFromFile}
               onClick={() => {
                  handlePatientReplaceReject();
                  dispatchSnackbarPositiveEvent(downloadPersonalDataFromEwusTranslationKeys.patientNotReplaced);
               }}
            />
            <ModalConfirmButton
               label={downloadPersonalDataFromEwusTranslationKeys.replace}
               onClick={() => {
                  props.onPatientReplace(props.ewusPatient);
                  dispatchSnackbarPositiveEvent(
                     downloadPersonalDataFromEwusTranslationKeys.patientReplacedWithEwusPatient
                  );
                  closeComparisonModal();
               }}
            />
            <ModalCloseButton onClick={handlePatientReplaceReject} />
         </>
      </EwusPatientComparisonModal>
   );
};
